<template>
  <div class="bg-white rounded-xl shadow-md p-5 h-full w-80 flex flex-col justify-between">
    <div class="overflow-hidden">
      <!-- All Accounts -->
      <div class="border-b border-gray-200 pb-3 mb-3">
        <div
          class="flex items-center justify-between flex-wrap gap-1 py-2 px-3 rounded-lg cursor-pointer hover:bg-gray-100"
          :class="[viewing_all_accounts ? 'bg-gray-50' : '']"
          @click="handleAccountClick({ id: 'all-accounts', name: 'All Accounts' })"
        >
          <div class="font-medium whitespace-nowrap">All accounts</div>
          <div class="text-black dark:text-white font-semibold tracking-wide flex-1 text-end">
            {{ aggregated_balance_formatted }}
          </div>
        </div>
      </div>

      <!-- Each Account -->
      <div :class="[groupedAccounts.length > 0 && groupedAccounts[0].name != 'Other' ? 'divide-y divide-gray-200' : '']">
        <div v-for="group in groupedAccounts" :key="group.name" :class="[groupedAccounts.length > 0 && groupedAccounts[0].name != 'Other' ? 'py-3 first:pt-0' : '']">
          <div v-if="groupedAccounts.length > 0 && groupedAccounts[0].name != 'Other'" class="px-2 pb-1 flex justify-between items-center">
            <div class="text-sm font-semibold text-gray-800 w-fit">{{ group.name }}</div>
            <div class="flex-grow border-b border-dashed border-gray-200 mx-3"></div>
            <div class="text-xs font-medium text-gray-400 w-fit">{{ dollarize(group.group_balance, includeDecimals(group.group_balance)) }}</div>
          </div>

          <div v-for="account in group.accounts" :key="account.id">
            <div
              class="flex items-center justify-between gap-1 mt-2 py-2 px-3 rounded-lg cursor-pointer hover:bg-gray-100"
              :class="[account.viewing ? 'bg-gray-100' : '', groupedAccounts.length > 0 && groupedAccounts[0].name != 'Other' ? 'first:mt-0' : '']"
              @click="handleAccountClick(account)"
            >
              <div class="flex flex-col w-52">
                <div class="whitespace-nowrap max-w-48 truncate text-gray-600">
                  {{ account.name }}
                </div>
                <div class="text-gray-500 text-xs">
                  {{ account.tapi_account_id }}
                </div>
              </div>

              <div class="flex flex-col">
                <div class="text-gray-900 dark:text-neutral-100 font-medium tracking-wide flex-1 text-end">
                  {{ dollarize(balances[account.id], includeDecimals(balances[account.id])) }}
                </div>
                <AppBadge :text="account.type" :adjust="true" size="sm" :color_identifier="account.type" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Stuff -->
    <div class="px-3 flex flex-col gap-3 mt-5">
      <div v-if="can_transfer_funds" class="flex gap-2 items-center cursor-pointer text-gray-600 hover:text-black w-fit" @click="emit('transfer')">
        <ArrowsRightLeftIcon class="size-5" />
        <div>Transfer Funds</div>
      </div>
      <div class="flex gap-2 items-center cursor-pointer text-gray-600 hover:text-black w-fit" @click="emit('link')">
        <LinkIcon class="size-5" />
        <div>Link an external account</div>
      </div>
      <div class="flex gap-2 items-center cursor-pointer text-gray-600 hover:text-black w-fit" @click="emit('open')">
        <FolderPlusIcon class="size-5" />
        <div>Open an account</div>
      </div>
      <div class="flex gap-2 items-center cursor-pointer text-gray-600 hover:text-black w-fit" @click="emit('settings')">
        <Cog8ToothIcon class="size-5" />
        <div>Settings</div>
      </div>
    </div>
  </div>
</template>

<script setup>
// Essentials
import { onMounted, computed, ref } from "vue";

// Stores
import { useAccountsStore } from "@/stores/accounts.js";
const accountsStore = useAccountsStore();

// Components
import AppBadge from "@/components/applicationui/AppBadge.vue";

// Icons
import { LinkIcon, FolderPlusIcon, ArrowsRightLeftIcon, Cog8ToothIcon } from "@heroicons/vue/24/outline";

// Formatting
import { dollarize } from "@/helper/format.js";

// Emit
const emit = defineEmits(["open", "link", "transfer", "view", "settings"]);

// Props
const props = defineProps({
  accounts: {
    type: Array,
    required: true,
    default: () => [],
  },
});

// Handle Account Selection START
const handleAccountClick = (account) => {
  setAccountViewingClass(account);
  if (account.id === "all-accounts") {
    emit("view", props.accounts);
    return;
  }
  emit("view", [account]);
};
const viewing_all_accounts = computed(() => {
  return props.accounts.every((account) => !account.viewing);
});
const setAccountViewingClass = (account) => {
  props.accounts.forEach((acc) => {
    acc.viewing = acc.id === account.id;
  });
};
// Get Account Balances START
const aggregated_balance_formatted = computed(() => {
  return dollarize(aggregated_balance.value, includeDecimals(aggregated_balance.value));
});
function includeDecimals(value) {
  return value < 1000;
}
const balances = ref({});
const aggregated_balance = ref(0);
const getAccountBalances = async (allMyAccounts) => {
  allMyAccounts.forEach(async (account) => {
    const balance = await accountsStore.getAccountBalance(account.id);
    balances.value[account.id] = balance;
    aggregated_balance.value += balance;
  });
};
// Get Account Balances END

// Array of grouped Accounts START
// Group accounts and calculate group balances
const groupedAccounts = computed(() => {
  const groups = props.accounts.reduce((acc, account) => {
    const groupName = account.display_group || "Other";
    if (!acc[groupName]) acc[groupName] = { name: groupName, accounts: [], group_balance: 0 };
    acc[groupName].accounts.push(account);

    // Add account's balance to the group's total balance
    const accountBalance = balances.value[account.id] || 0;
    acc[groupName].group_balance += accountBalance;

    return acc;
  }, {});

  return Object.values(groups).sort((a, b) => {
    if (a.name === "Other") return 1;
    if (b.name === "Other") return -1;
    return a.name.localeCompare(b.name);
  });
});
// Array of grouped Accounts End

// Can Transfer Funds START
const can_transfer_funds = computed(() => {
  if (props.accounts.length > 1) return true;
  if (props.accounts.length === 1 && props.accounts[0].has_linked_external_account) return true;
  return false
});
// Can Transfer Funds END

// Mounted START
onMounted(async () => {
  // Get account balances
  await getAccountBalances(props.accounts);
});
// Mounted END
</script>
